// Dates in the team's time zone (REMINDER_TZ, America/New_York by default): where weekends start
// and end, and how dates are written. Pure, Intl only: the server and the browser both use it.

export const DAY = 86_400_000;
const WEEKDAY: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
const partFmt = new Map<string, Intl.DateTimeFormat>();

export const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** Calendar date, weekday (0 = Sunday) and clock time of an instant in a time zone. */
export function localParts(t: number, timeZone: string) {
  let f = partFmt.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      weekday: "short",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    partFmt.set(timeZone, f);
  }
  const parts = f.formatToParts(new Date(t));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "0";
  return {
    y: Number(get("year")),
    m: Number(get("month")),
    d: Number(get("day")),
    wd: WEEKDAY[get("weekday")] ?? 0,
    h: Number(get("hour")) % 24,
    min: Number(get("minute")),
    s: Number(get("second")),
  };
}

/**
 * The instant a wall-clock time happens in a time zone: midnight of Oct 2 in New York is 04:00 UTC
 * in summer and 05:00 UTC in winter. Days roll over (d = 0 is the last day of the month before).
 */
export function zonedTime(y: number, m: number, d: number, timeZone: string, h = 0, min = 0): Date {
  const want = Date.UTC(y, m - 1, d, h, min);
  let t = want;
  // twice: the offset can differ either side of a clock change
  for (let i = 0; i < 2; i++) {
    const p = localParts(t, timeZone);
    t += want - Date.UTC(p.y, p.m - 1, p.d, p.h, p.min, p.s);
  }
  return new Date(t);
}

/** Midnight at the start of the local day `back` calendar days before the one `now` is in. */
export function startOfDay(now: Date, timeZone: string, back = 0): Date {
  const p = localParts(now.getTime(), timeZone);
  return zonedTime(p.y, p.m, p.d - back, timeZone);
}

/** The local calendar date of an instant, as "2026-10-02". */
export function localDate(t: Date, timeZone: string): string {
  const p = localParts(t.getTime(), timeZone);
  return `${p.y}-${String(p.m).padStart(2, "0")}-${String(p.d).padStart(2, "0")}`;
}

/** "Sep 24" in the team's time zone. */
export function dayLabel(d: Date, timeZone: string): string {
  const p = localParts(d.getTime(), timeZone);
  return `${MONTHS[p.m - 1]} ${p.d}`;
}

/** "Tue Sep 29" in the team's time zone. */
export function weekdayLabel(d: Date, timeZone: string): string {
  return `${WEEKDAYS[localParts(d.getTime(), timeZone).wd]} ${dayLabel(d, timeZone)}`;
}

/** "Oct 5, 14:05" in the team's time zone. */
export function dayTimeLabel(d: Date, timeZone: string): string {
  const p = localParts(d.getTime(), timeZone);
  return `${MONTHS[p.m - 1]} ${p.d}, ${String(p.h).padStart(2, "0")}:${String(p.min).padStart(2, "0")}`;
}
